import type { ReactNode } from 'react'
import { AssistantRuntimeProvider } from '@assistant-ui/react'
import { useDataStreamRuntime } from '@assistant-ui/react-data-stream'

import { idToken } from '../auth/firebase'

import { ChooseSetupPathToolUI } from './ChooseSetupPathToolUI'
import { ConfigureCardToolUI } from './ConfigureCardToolUI'
import { ConnectBankToolUI } from './ConnectBankToolUI'
import { EnterAccountToolUI } from './EnterAccountToolUI'
import { SelectAccountsToolUI } from './SelectAccountsToolUI'

/**
 * The assistant runtime, mounted around the whole application.
 *
 * Around the *whole* application deliberately, even though the only thing that renders a
 * conversation today is onboarding. Copilot features — letting the assistant read what is
 * on screen and act on it — need the provider at or above the tree they touch, so putting
 * it inside a modal now would mean moving it later and rewiring everything that had come
 * to depend on where it sat.
 *
 * Tool UIs are registered here rather than next to the component that happens to use them,
 * because registration is global to the runtime: a tool call can arrive in any turn, and a
 * renderer that mounts only alongside some particular screen would miss it.
 */
export function FluenceAssistant({ children }: { children: ReactNode }) {
  const runtime = useDataStreamRuntime({
    api: '/api/assistant/chat',
    // The generated client attaches this through its mutator; the assistant stream is the
    // one request that does not go through it, so it attaches its own. A function rather
    // than a value, because the SDK refreshes the token roughly hourly and a conversation
    // can outlive one.
    headers: async (): Promise<Record<string, string>> => {
      const token = idToken()
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    // A scripted flow is nothing but tool calls and continuations, so the default of a
    // single step would stop the conversation the moment the user answered anything.
    maxSteps: 12,
  })

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ChooseSetupPathToolUI />
      <ConnectBankToolUI />
      <SelectAccountsToolUI />
      <ConfigureCardToolUI />
      <EnterAccountToolUI />
      {children}
    </AssistantRuntimeProvider>
  )
}
